import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useActiveMunicipio } from '@/context/ActiveMunicipioContext';
import CarreirasGrafico from './CarreirasGrafico';

interface Carreira {
  id: string;
  nome: string;
  descricao: string | null;
  municipio_id: string | null;
  ativo: boolean;
  ordem: number;
  nivel_maximo: number | null;
}

interface CarreiraNivel {
  id: string;
  carreira_id: string;
  nivel: number;
  designacao: string;
  vencimento_base: number | null;
  ordem: number;
}

interface CarreiraFormState {
  nome: string;
  descricao: string;
  ativo: boolean;
  nivel_maximo: string;
}

interface NivelFormState {
  nivel: string;
  designacao: string;
  vencimento_base: string;
}

const EMPTY_FORM: CarreiraFormState = { nome: '', descricao: '', ativo: true, nivel_maximo: '' };
const EMPTY_NIVEL: NivelFormState = { nivel: '', designacao: '', vencimento_base: '' };

export default function CarreirasTab() {
  const { activeMunicipio } = useActiveMunicipio();
  const [carreiras, setCarreiras] = useState<Carreira[]>([]);
  const [niveis, setNiveis] = useState<CarreiraNivel[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Modal carreira
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState<Carreira | null>(null);
  const [form, setForm] = useState<CarreiraFormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Modal níveis
  const [niveisCarreira, setNiveisCarreira] = useState<Carreira | null>(null);
  const [showNivelModal, setShowNivelModal] = useState(false);
  const [editNivel, setEditNivel] = useState<CarreiraNivel | null>(null);
  const [nivelForm, setNivelForm] = useState<NivelFormState>(EMPTY_NIVEL);
  const [savingNivel, setSavingNivel] = useState(false);
  const [deletingNivelId, setDeletingNivelId] = useState<string | null>(null);

  // Outros
  const [confirmDelete, setConfirmDelete] = useState<Carreira | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [reorderingId, setReorderingId] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [vistaGrafico, setVistaGrafico] = useState(false);

  const municipioId = activeMunicipio?.id ?? null;

  const load = useCallback(async () => {
    setLoading(true);
    const query = supabase.from('carreiras').select('*').order('ordem');
    const { data: cData } = municipioId
      ? await query.or(`municipio_id.is.null,municipio_id.eq.${municipioId}`)
      : await query.is('municipio_id', null);

    if (cData) setCarreiras(cData as Carreira[]);

    // Carregar todos os níveis das carreiras visíveis
    if (cData && cData.length > 0) {
      const ids = cData.map((c) => c.id);
      const { data: nData } = await supabase
        .from('carreira_niveis')
        .select('*')
        .in('carreira_id', ids)
        .order('ordem');
      if (nData) setNiveis(nData as CarreiraNivel[]);
    } else {
      setNiveis([]);
    }
    setLoading(false);
  }, [municipioId]);

  useEffect(() => { load(); }, [load]);

  function showMsg(type: 'success' | 'error', text: string) {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  }

  // ── Carreira CRUD ──────────────────────────────────────────────

  function openCreate() {
    setEditItem(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  }

  function openEdit(c: Carreira) {
    setEditItem(c);
    setForm({
      nome: c.nome,
      descricao: c.descricao ?? '',
      ativo: c.ativo,
      nivel_maximo: c.nivel_maximo != null ? String(c.nivel_maximo) : '',
    });
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditItem(null);
    setForm(EMPTY_FORM);
  }

  async function handleSave() {
    if (!form.nome.trim()) return;
    setSaving(true);
    try {
      const nivelMaxVal = form.nivel_maximo.trim() ? parseInt(form.nivel_maximo) : null;
      if (editItem) {
        const { error } = await supabase
          .from('carreiras')
          .update({
            nome: form.nome.trim(),
            descricao: form.descricao.trim() || null,
            ativo: form.ativo,
            nivel_maximo: nivelMaxVal,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editItem.id);
        if (error) throw error;
        showMsg('success', 'Carreira atualizada com sucesso!');
      } else {
        const maxOrdem = carreiras.length > 0 ? Math.max(...carreiras.map((c) => c.ordem)) + 1 : 1;
        const { error } = await supabase.from('carreiras').insert({
          nome: form.nome.trim(),
          descricao: form.descricao.trim() || null,
          ativo: form.ativo,
          municipio_id: municipioId,
          ordem: maxOrdem,
          nivel_maximo: nivelMaxVal,
        });
        if (error) throw error;
        showMsg('success', 'Carreira criada com sucesso!');
      }
      await load();
      closeModal();
    } catch (err: unknown) {
      showMsg('error', err instanceof Error ? err.message : 'Erro ao guardar.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(c: Carreira) {
    setDeletingId(c.id);
    try {
      const { error } = await supabase.from('carreiras').delete().eq('id', c.id);
      if (error) throw error;
      showMsg('success', `"${c.nome}" eliminada.`);
      await load();
    } catch (err: unknown) {
      showMsg('error', err instanceof Error ? err.message : 'Erro ao eliminar.');
    } finally {
      setDeletingId(null);
      setConfirmDelete(null);
    }
  }

  async function handleToggleAtivo(c: Carreira) {
    try {
      const { error } = await supabase
        .from('carreiras')
        .update({ ativo: !c.ativo, updated_at: new Date().toISOString() })
        .eq('id', c.id);
      if (error) throw error;
      showMsg('success', `Carreira ${!c.ativo ? 'ativada' : 'desativada'}.`);
      await load();
    } catch (err: unknown) {
      showMsg('error', err instanceof Error ? err.message : 'Erro ao alterar estado.');
    }
  }

  async function handleReorder(id: string, direction: 'up' | 'down') {
    const sorted = [...carreiras].sort((a, b) => a.ordem - b.ordem);
    const idx = sorted.findIndex((c) => c.id === id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const a = sorted[idx];
    const b = sorted[swapIdx];
    setReorderingId(id);
    try {
      await Promise.all([
        supabase.from('carreiras').update({ ordem: b.ordem }).eq('id', a.id),
        supabase.from('carreiras').update({ ordem: a.ordem }).eq('id', b.id),
      ]);
      await load();
    } catch (err: unknown) {
      showMsg('error', err instanceof Error ? err.message : 'Erro ao reordenar.');
    } finally {
      setReorderingId(null);
    }
  }

  // ── Níveis CRUD ────────────────────────────────────────────────

  function openNiveis(c: Carreira) {
    setNiveisCarreira(c);
  }

  function closeNiveis() {
    setNiveisCarreira(null);
    setShowNivelModal(false);
    setEditNivel(null);
    setNivelForm(EMPTY_NIVEL);
  }

  function openCreateNivel() {
    setEditNivel(null);
    const existingNiveis = niveis.filter((n) => n.carreira_id === niveisCarreira?.id);
    const nextNivel = existingNiveis.length > 0 ? Math.max(...existingNiveis.map((n) => n.nivel)) + 1 : 1;
    setNivelForm({ nivel: String(nextNivel), designacao: `Nível ${nextNivel}`, vencimento_base: '' });
    setShowNivelModal(true);
  }

  function openEditNivel(n: CarreiraNivel) {
    setEditNivel(n);
    setNivelForm({
      nivel: String(n.nivel),
      designacao: n.designacao,
      vencimento_base: n.vencimento_base != null ? String(n.vencimento_base) : '',
    });
    setShowNivelModal(true);
  }

  function closeNivelModal() {
    setShowNivelModal(false);
    setEditNivel(null);
    setNivelForm(EMPTY_NIVEL);
  }

  async function handleSaveNivel() {
    if (!nivelForm.nivel.trim() || !nivelForm.designacao.trim() || !niveisCarreira) return;
    setSavingNivel(true);
    try {
      const nivelNum = parseInt(nivelForm.nivel);
      const vencBase = nivelForm.vencimento_base.trim() ? parseFloat(nivelForm.vencimento_base) : null;
      if (editNivel) {
        const { error } = await supabase
          .from('carreira_niveis')
          .update({
            nivel: nivelNum,
            designacao: nivelForm.designacao.trim(),
            vencimento_base: vencBase,
            ordem: nivelNum,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editNivel.id);
        if (error) throw error;
        showMsg('success', 'Nível atualizado!');
      } else {
        const { error } = await supabase.from('carreira_niveis').insert({
          carreira_id: niveisCarreira.id,
          nivel: nivelNum,
          designacao: nivelForm.designacao.trim(),
          vencimento_base: vencBase,
          ordem: nivelNum,
        });
        if (error) throw error;
        showMsg('success', 'Nível criado!');
      }
      await load();
      closeNivelModal();
    } catch (err: unknown) {
      showMsg('error', err instanceof Error ? err.message : 'Erro ao guardar nível.');
    } finally {
      setSavingNivel(false);
    }
  }

  async function handleDeleteNivel(n: CarreiraNivel) {
    setDeletingNivelId(n.id);
    try {
      const { error } = await supabase.from('carreira_niveis').delete().eq('id', n.id);
      if (error) throw error;
      showMsg('success', `Nível ${n.nivel} eliminado.`);
      await load();
    } catch (err: unknown) {
      showMsg('error', err instanceof Error ? err.message : 'Erro ao eliminar nível.');
    } finally {
      setDeletingNivelId(null);
    }
  }

  // ── Render ─────────────────────────────────────────────────────

  const filtered = carreiras.filter((c) =>
    c.nome.toLowerCase().includes(search.toLowerCase().trim()) ||
    (c.descricao ?? '').toLowerCase().includes(search.toLowerCase().trim())
  );
  const sorted = [...filtered].sort((a, b) => a.ordem - b.ordem);
  const totalAtivas = carreiras.filter((c) => c.ativo).length;
  const totalInativas = carreiras.filter((c) => !c.ativo).length;

  // Vista de níveis de uma carreira
  if (niveisCarreira) {
    const carreiraNiveis = niveis
      .filter((n) => n.carreira_id === niveisCarreira.id)
      .sort((a, b) => a.ordem - b.ordem);

    return (
      <div className="space-y-5">
        {/* Feedback */}
        {msg && (
          <div className={`flex items-center gap-2 p-3 rounded-xl border ${msg.type === 'success' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className={`text-sm ${msg.type === 'success' ? 'ri-checkbox-circle-line text-emerald-500' : 'ri-error-warning-line text-red-500'}`} />
            </div>
            <p className={`text-sm ${msg.type === 'success' ? 'text-emerald-700' : 'text-red-700'}`}>{msg.text}</p>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <button
                onClick={closeNiveis}
                className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                title="Voltar às carreiras"
              >
                <i className="ri-arrow-left-line text-base" />
              </button>
              <div className="w-8 h-8 flex items-center justify-center bg-slate-100 rounded-lg flex-shrink-0">
                <i className="ri-list-ordered text-slate-600 text-base" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-slate-800">
                  Níveis — <span className="text-slate-500">{niveisCarreira.nome}</span>
                </h2>
                <p className="text-xs text-slate-400 mt-0.5">
                  {carreiraNiveis.length} nível{carreiraNiveis.length !== 1 ? 'is' : ''}
                  {niveisCarreira.nivel_maximo != null && (
                    <span className="ml-1">· Nível máximo: <strong className="text-slate-600">{niveisCarreira.nivel_maximo}</strong></span>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={openCreateNivel}
              className="whitespace-nowrap flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors cursor-pointer"
            >
              <i className="ri-add-line" />
              Novo Nível
            </button>
          </div>

          {/* Tabela de níveis */}
          {carreiraNiveis.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3 text-slate-400">
              <div className="w-12 h-12 flex items-center justify-center bg-slate-50 rounded-full">
                <i className="ri-list-ordered text-2xl" />
              </div>
              <p className="text-sm">Nenhum nível definido para esta carreira</p>
              <button onClick={openCreateNivel} className="whitespace-nowrap text-xs text-slate-500 hover:text-slate-800 underline cursor-pointer">
                Criar primeiro nível
              </button>
            </div>
          ) : (
            <>
              {/* Cabeçalho tabela */}
              <div className="grid grid-cols-12 gap-3 px-6 py-2.5 bg-slate-50 border-b border-slate-100 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                <div className="col-span-1">Nível</div>
                <div className="col-span-5">Designação</div>
                <div className="col-span-4">Vencimento Base</div>
                <div className="col-span-2 text-right">Ações</div>
              </div>
              <div className="divide-y divide-slate-50">
                {carreiraNiveis.map((n) => {
                  const isNivelMax = niveisCarreira.nivel_maximo != null && n.nivel === niveisCarreira.nivel_maximo;
                  return (
                    <div key={n.id} className="grid grid-cols-12 gap-3 px-6 py-3.5 items-center hover:bg-slate-50/60 transition-colors group">
                      <div className="col-span-1">
                        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold ${
                          isNivelMax ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {n.nivel}
                        </span>
                      </div>
                      <div className="col-span-5 flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-700">{n.designacao}</span>
                        {isNivelMax && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full flex-shrink-0">
                            Máximo
                          </span>
                        )}
                      </div>
                      <div className="col-span-4">
                        {n.vencimento_base != null ? (
                          <span className="text-sm text-slate-600 font-mono">
                            {Number(n.vencimento_base).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-300 italic">Não definido</span>
                        )}
                      </div>
                      <div className="col-span-2 flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => openEditNivel(n)}
                          className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                          title="Editar nível"
                        >
                          <i className="ri-edit-line text-sm" />
                        </button>
                        <button
                          onClick={() => handleDeleteNivel(n)}
                          disabled={deletingNivelId === n.id}
                          className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
                          title="Eliminar nível"
                        >
                          {deletingNivelId === n.id
                            ? <i className="ri-loader-4-line text-sm animate-spin" />
                            : <i className="ri-delete-bin-line text-sm" />
                          }
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Resumo */}
              <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/50 flex items-center gap-6 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">Total de níveis:</span>
                  <span className="text-xs font-semibold text-slate-700">{carreiraNiveis.length}</span>
                </div>
                {niveisCarreira.nivel_maximo != null && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">Nível máximo configurado:</span>
                    <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full">{niveisCarreira.nivel_maximo}</span>
                  </div>
                )}
                {carreiraNiveis.some((n) => n.vencimento_base != null) && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">Vencimento base máx.:</span>
                    <span className="text-xs font-semibold text-slate-700 font-mono">
                      {Math.max(...carreiraNiveis.filter((n) => n.vencimento_base != null).map((n) => Number(n.vencimento_base))).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })}
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Modal criar/editar nível */}
        {showNivelModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={closeNivelModal}>
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
            <div className="relative bg-white rounded-2xl w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-slate-800 text-base">
                    {editNivel ? 'Editar Nível' : 'Novo Nível'}
                  </h3>
                  <p className="text-xs text-slate-400 mt-0.5">{niveisCarreira?.nome}</p>
                </div>
                <button onClick={closeNivelModal} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer">
                  <i className="ri-close-line text-lg" />
                </button>
              </div>
              <div className="px-6 py-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                      Nº do Nível <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={nivelForm.nivel}
                      onChange={(e) => setNivelForm((f) => ({ ...f, nivel: e.target.value }))}
                      placeholder="Ex: 1"
                      autoFocus
                      className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 placeholder-slate-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                      Vencimento Base (€)
                    </label>
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      value={nivelForm.vencimento_base}
                      onChange={(e) => setNivelForm((f) => ({ ...f, vencimento_base: e.target.value }))}
                      placeholder="Ex: 1201.48"
                      className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 placeholder-slate-400"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                    Designação <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={nivelForm.designacao}
                    onChange={(e) => setNivelForm((f) => ({ ...f, designacao: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveNivel(); }}
                    placeholder="Ex: Nível 1"
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 placeholder-slate-400"
                  />
                </div>
              </div>
              <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3">
                <button onClick={closeNivelModal} className="whitespace-nowrap px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">Cancelar</button>
                <button
                  onClick={handleSaveNivel}
                  disabled={savingNivel || !nivelForm.nivel.trim() || !nivelForm.designacao.trim()}
                  className="whitespace-nowrap flex items-center gap-2 px-5 py-2 text-sm bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50 cursor-pointer"
                >
                  {savingNivel ? <><i className="ri-loader-4-line animate-spin" />A guardar...</> : <><i className="ri-save-line" />Guardar</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Vista principal: lista de carreiras ────────────────────────
  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-slate-100 rounded-lg flex-shrink-0">
              <i className="ri-briefcase-line text-slate-600 text-base" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-800">Carreiras</h2>
              <p className="text-xs text-slate-400 mt-0.5">
                {carreiras.length} carreira{carreiras.length !== 1 ? 's' : ''} ·{' '}
                <span className="text-emerald-600">{totalAtivas} ativa{totalAtivas !== 1 ? 's' : ''}</span>
                {totalInativas > 0 && (
                  <span className="text-slate-400"> · {totalInativas} inativa{totalInativas !== 1 ? 's' : ''}</span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={openCreate}
            className="whitespace-nowrap flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors cursor-pointer"
          >
            <i className="ri-add-line" />
            Nova Carreira
          </button>
        </div>

        {/* Pesquisa */}
        <div className="px-4 py-3 border-b border-slate-100">
          <div className="relative">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <i className="ri-search-line text-sm text-slate-400" />
            </div>
            <input
              type="text"
              placeholder="Pesquisar carreiras..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-slate-400 hover:text-slate-700 cursor-pointer">
                <i className="ri-close-line text-sm" />
              </button>
            )}
          </div>
        </div>

        {/* Feedback */}
        {msg && (
          <div className={`mx-6 mt-3 flex items-center gap-2 p-3 rounded-lg border ${msg.type === 'success' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className={`text-sm ${msg.type === 'success' ? 'ri-checkbox-circle-line text-emerald-500' : 'ri-error-warning-line text-red-500'}`} />
            </div>
            <p className={`text-sm ${msg.type === 'success' ? 'text-emerald-700' : 'text-red-700'}`}>{msg.text}</p>
          </div>
        )}

        {/* Info */}
        <div className="px-6 py-2.5 border-b border-slate-50 flex items-center gap-2 bg-slate-50/50">
          <i className="ri-information-line text-slate-400 text-xs" />
          <p className="text-xs text-slate-400">
            Carreiras sem módulo associado são globais (partilhadas por todos os municípios). Clica em <strong className="text-slate-500">Níveis</strong> para gerir os escalões de cada carreira.
          </p>
        </div>

        {/* Lista */}
        <div className="divide-y divide-slate-50 pb-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <i className="ri-loader-4-line text-2xl text-slate-300 animate-spin" />
            </div>
          ) : sorted.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3 text-slate-400">
              <div className="w-12 h-12 flex items-center justify-center bg-slate-50 rounded-full">
                <i className="ri-briefcase-line text-2xl" />
              </div>
              {search ? (
                <>
                  <p className="text-sm">Nenhuma carreira encontrada para &quot;{search}&quot;</p>
                  <button onClick={() => setSearch('')} className="whitespace-nowrap text-xs underline cursor-pointer">Limpar pesquisa</button>
                </>
              ) : (
                <>
                  <p className="text-sm">Nenhuma carreira criada</p>
                  <button onClick={openCreate} className="whitespace-nowrap text-xs underline cursor-pointer">Criar primeira carreira</button>
                </>
              )}
            </div>
          ) : (
            sorted.map((c, idx) => {
              const isFirst = idx === 0;
              const isLast = idx === sorted.length - 1;
              const isReordering = reorderingId === c.id;
              const isGlobal = c.municipio_id === null;
              const carreiraNiveisCount = niveis.filter((n) => n.carreira_id === c.id).length;

              return (
                <div
                  key={c.id}
                  className={`flex items-center gap-3 px-6 py-4 hover:bg-slate-50/60 transition-colors group ${!c.ativo ? 'opacity-60' : ''}`}
                >
                  {/* Ícone */}
                  <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${c.ativo ? 'bg-slate-100' : 'bg-slate-50 border border-slate-200'}`}>
                    <i className={`ri-briefcase-line text-sm ${c.ativo ? 'text-slate-600' : 'text-slate-400'}`} />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => openEdit(c)}
                        className="text-sm font-semibold text-slate-800 hover:underline underline-offset-2 decoration-slate-400/50 cursor-pointer text-left"
                      >
                        {c.nome}
                      </button>
                      {isGlobal && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded-full flex-shrink-0">Global</span>
                      )}
                      {!c.ativo && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-slate-100 text-slate-400 rounded-full flex-shrink-0">Inativa</span>
                      )}
                      {c.nivel_maximo != null && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded-full flex-shrink-0">
                          Máx. nível {c.nivel_maximo}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                      {c.descricao && (
                        <p className="text-xs text-slate-400 truncate max-w-xs">{c.descricao}</p>
                      )}
                      <button
                        onClick={() => openNiveis(c)}
                        className="whitespace-nowrap flex items-center gap-1 text-xs text-slate-400 hover:text-slate-700 cursor-pointer transition-colors"
                      >
                        <i className="ri-list-ordered text-xs" />
                        {carreiraNiveisCount > 0 ? `${carreiraNiveisCount} nível${carreiraNiveisCount !== 1 ? 'is' : ''}` : 'Sem níveis'}
                      </button>
                    </div>
                  </div>

                  {/* Reordenar */}
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    <button onClick={() => handleReorder(c.id, 'up')} disabled={isFirst || isReordering} title="Mover para cima"
                      className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors cursor-pointer disabled:opacity-20 disabled:cursor-not-allowed">
                      {isReordering ? <i className="ri-loader-4-line text-xs animate-spin" /> : <i className="ri-arrow-up-s-line text-sm" />}
                    </button>
                    <button onClick={() => handleReorder(c.id, 'down')} disabled={isLast || isReordering} title="Mover para baixo"
                      className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors cursor-pointer disabled:opacity-20 disabled:cursor-not-allowed">
                      {isReordering ? <i className="ri-loader-4-line text-xs animate-spin" /> : <i className="ri-arrow-down-s-line text-sm" />}
                    </button>
                    <div className="w-px h-4 bg-slate-200 mx-1" />
                  </div>

                  {/* Ações */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    <button onClick={() => openNiveis(c)} title="Gerir níveis"
                      className="whitespace-nowrap flex items-center gap-1 px-2.5 py-1.5 text-xs text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer">
                      <i className="ri-list-ordered text-xs" />
                      Níveis
                    </button>
                    <button onClick={() => handleToggleAtivo(c)} title={c.ativo ? 'Desativar' : 'Ativar'}
                      className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${c.ativo ? 'text-slate-400 hover:text-amber-600 hover:bg-amber-50' : 'text-slate-400 hover:text-emerald-600 hover:bg-emerald-50'}`}>
                      <i className={`text-sm ${c.ativo ? 'ri-eye-off-line' : 'ri-eye-line'}`} />
                    </button>
                    <button onClick={() => openEdit(c)} title="Editar"
                      className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer">
                      <i className="ri-edit-line text-sm" />
                    </button>
                    <button onClick={() => setConfirmDelete(c)} disabled={deletingId === c.id} title="Eliminar"
                      className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer disabled:opacity-50">
                      {deletingId === c.id ? <i className="ri-loader-4-line text-sm animate-spin" /> : <i className="ri-delete-bin-line text-sm" />}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Stats + Gráfico */}
      {carreiras.length > 0 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-slate-100 rounded-lg flex-shrink-0">
                <i className="ri-briefcase-line text-slate-600 text-base" />
              </div>
              <div>
                <p className="text-xl font-bold text-slate-800">{carreiras.length}</p>
                <p className="text-xs text-slate-400">Total de carreiras</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-emerald-50 rounded-lg flex-shrink-0">
                <i className="ri-checkbox-circle-line text-emerald-500 text-base" />
              </div>
              <div>
                <p className="text-xl font-bold text-slate-800">{totalAtivas}</p>
                <p className="text-xs text-slate-400">Carreiras ativas</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-slate-50 rounded-lg flex-shrink-0">
                <i className="ri-list-ordered text-slate-400 text-base" />
              </div>
              <div>
                <p className="text-xl font-bold text-slate-800">{niveis.length}</p>
                <p className="text-xs text-slate-400">Níveis definidos</p>
              </div>
            </div>
          </div>

          {/* Gráfico de evolução salarial */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-slate-100 rounded-lg flex-shrink-0">
                  <i className="ri-line-chart-line text-slate-600 text-base" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">Evolução Salarial por Carreira</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Curva de vencimentos base ao longo dos níveis</p>
                </div>
              </div>
              <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
                <button
                  onClick={() => setVistaGrafico(false)}
                  className={`whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer ${
                    !vistaGrafico ? 'bg-white text-slate-800' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <i className="ri-table-line text-xs" />
                  Tabela
                </button>
                <button
                  onClick={() => setVistaGrafico(true)}
                  className={`whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer ${
                    vistaGrafico ? 'bg-white text-slate-800' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <i className="ri-line-chart-line text-xs" />
                  Gráfico
                </button>
              </div>
            </div>

            <div className="p-6">
              {vistaGrafico ? (
                <CarreirasGrafico carreiras={carreiras} niveis={niveis} />
              ) : (
                /* Vista tabela comparativa */
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider pb-3 pr-4">Nível</th>
                        {carreiras.filter((c) => c.ativo && niveis.some((n) => n.carreira_id === c.id && n.vencimento_base != null)).map((c) => (
                          <th key={c.id} className="text-right text-xs font-semibold text-slate-600 uppercase tracking-wider pb-3 px-3">{c.nome}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {Array.from(new Set(niveis.map((n) => n.nivel))).sort((a, b) => a - b).map((nivel) => {
                        const carreirasAtivas = carreiras.filter((c) => c.ativo && niveis.some((n) => n.carreira_id === c.id && n.vencimento_base != null));
                        return (
                          <tr key={nivel} className="hover:bg-slate-50/60 transition-colors">
                            <td className="py-2.5 pr-4">
                              <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold bg-slate-100 text-slate-600">{nivel}</span>
                            </td>
                            {carreirasAtivas.map((c) => {
                              const nivelData = niveis.find((n) => n.carreira_id === c.id && n.nivel === nivel);
                              return (
                                <td key={c.id} className="py-2.5 px-3 text-right">
                                  {nivelData?.vencimento_base != null ? (
                                    <span className="text-sm font-mono font-semibold text-slate-700">
                                      {Number(nivelData.vencimento_base).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-slate-300">—</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Modal criar/editar carreira */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={closeModal}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-slate-800 text-base">{editItem ? 'Editar Carreira' : 'Nova Carreira'}</h3>
                <p className="text-xs text-slate-400 mt-0.5">{editItem ? `A editar: ${editItem.nome}` : 'Preenche os dados da nova carreira'}</p>
              </div>
              <button onClick={closeModal} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {/* Nome */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Nome da Carreira <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  value={form.nome}
                  onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
                  placeholder="Ex: Técnica Superior"
                  autoFocus
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 placeholder-slate-400"
                />
              </div>
              {/* Nível máximo */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Nível Máximo <span className="text-slate-400 font-normal">(opcional)</span>
                </label>
                <input
                  type="number"
                  min={1}
                  value={form.nivel_maximo}
                  onChange={(e) => setForm((f) => ({ ...f, nivel_maximo: e.target.value }))}
                  placeholder="Ex: 10"
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 placeholder-slate-400"
                />
                <p className="text-xs text-slate-400 mt-1">Nível máximo que um funcionário pode atingir nesta carreira. Usado para validação de progressões.</p>
              </div>
              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Descrição <span className="text-slate-400 font-normal">(opcional)</span></label>
                <textarea
                  value={form.descricao}
                  onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
                  placeholder="Breve descrição da carreira..."
                  rows={2}
                  maxLength={500}
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 placeholder-slate-400 resize-none"
                />
                <p className="text-xs text-slate-400 mt-1 text-right">{form.descricao.length}/500</p>
              </div>
              {/* Estado */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                <div>
                  <p className="text-sm font-medium text-slate-700">Carreira ativa</p>
                  <p className="text-xs text-slate-400 mt-0.5">Carreiras inativas não aparecem nos formulários</p>
                </div>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, ativo: !f.ativo }))}
                  className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${form.ativo ? 'bg-slate-900' : 'bg-slate-200'}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.ativo ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3">
              <button onClick={closeModal} className="whitespace-nowrap px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">Cancelar</button>
              <button onClick={handleSave} disabled={saving || !form.nome.trim()}
                className="whitespace-nowrap flex items-center gap-2 px-5 py-2 text-sm bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50 cursor-pointer">
                {saving ? <><i className="ri-loader-4-line animate-spin" />A guardar...</> : <><i className="ri-save-line" />Guardar</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmar eliminação */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setConfirmDelete(null)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-5">
              <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-full mx-auto mb-4">
                <i className="ri-delete-bin-line text-red-500 text-xl" />
              </div>
              <h3 className="font-bold text-slate-800 text-base text-center mb-1">Confirmar eliminação</h3>
              <p className="text-sm text-slate-500 text-center">
                Tens a certeza que queres eliminar a carreira{' '}
                <strong className="text-slate-700">&quot;{confirmDelete.nome}&quot;</strong>?
              </p>
              {confirmDelete.municipio_id === null && (
                <p className="text-xs text-amber-600 text-center mt-2 bg-amber-50 px-3 py-2 rounded-lg">
                  Esta é uma carreira global — a eliminação afeta todos os municípios.
                </p>
              )}
            </div>
            <div className="px-6 pb-5 flex items-center justify-center gap-3">
              <button onClick={() => setConfirmDelete(null)} className="whitespace-nowrap px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">Cancelar</button>
              <button onClick={() => handleDelete(confirmDelete)} disabled={deletingId === confirmDelete.id}
                className="whitespace-nowrap flex items-center gap-2 px-5 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 cursor-pointer">
                {deletingId === confirmDelete.id ? <><i className="ri-loader-4-line animate-spin" />A eliminar...</> : <><i className="ri-delete-bin-line" />Eliminar</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
